import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import { BOOKMARK_EVENTS } from "../../reducers/bookmark-reducer";

import { setIsBookmarkOpen } from "../../reducers/common-reducer";
import { addBookmark, getBookmark, tagBookmark } from "../../actions/bookmarkAction";
import useLanguageChange from "../../hooks/useLanguageChange";
import { ENABLED } from "../../constants/Constants";
import takePhotoService from "../../services/takePhotoService";
import { isEmpty } from "lodash";
import CustomToast from "../UI/customToast";
import { totalTimeInSeconds } from "../../shared/utility";
import { LogFirebase } from "../../services/firestore";

const UPLOAD_IMAGE_SUCCESS = "2";

const BookmarkDrawer = () => {
    const dispatch = useDispatch();
    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();
    const { isBookmarkOpen, allowTakePhoto, callDuration } = useSelector((state) => state.commonReducer);
    const { joinMeetingData } = useSelector((state) => state.joinMeeting);
    const {
        getBookmarkLoading,
        getBookmarkData,
        getBookmarkError,
        addBookmarkData,
        tagBookmarkData,
        bookmarkWithImage,
        tagBookmarkError,
        addBookmarkError,
    } = useSelector((state) => state.bookmark);
    const { screenSharingUsers, videoStreamingUsers } = useSelector((state) => state.twilioEvents);

    const [bookmarkText, setBookmarkText] = useState("");
    const [bookmarkList, setBookmarkList] = useState([]);

    /* Handle get bookmark */
    useEffect(() => {
        if (joinMeetingData) {
            getBookmarkHandler();
        }
    }, [joinMeetingData]);

    /* Handle to set a all bookmarks */
    useEffect(() => {
        if (getBookmarkData) {
            LogFirebase("Bookmarks", "totalCount", getBookmarkData?.items?.length);
            const bookmarks = JSON.parse(JSON.stringify(getBookmarkData?.items));
            setBookmarkList([...bookmarks]);
        }
    }, [getBookmarkData]);

    /* Handle add new bookmark in bookmark list */
    useEffect(() => {
        if (addBookmarkData) {
            LogFirebase("Bookmarks", "added_bookmark", addBookmarkData?.bookmarkName);
            clearBookmarkText();
            CustomToast.success(localisedValues["bookmark_added"]);
            if (addBookmarkData?.isLive) {
                setBookmarkList([addBookmarkData, ...bookmarkList]);
            }
        } else if (addBookmarkError) {
            LogFirebase("Bookmarks", "added_bookmark_error", addBookmarkError);
        }
    }, [addBookmarkData, addBookmarkError]);

    /* Handle tag bookmark and update bookmark list with count */
    useEffect(() => {
        if (tagBookmarkData) {
            if (tagBookmarkData?.success > 0) {
                const index = bookmarkList?.findIndex((element) => element?.bookmarkId === tagBookmarkData?.data?.bid);
                if (index >= 0) {
                    const bookMarkDetail = bookmarkList?.length > 0 ? bookmarkList[index] : null;
                    if (!isEmpty(bookMarkDetail)) {
                        LogFirebase("Bookmarks", "saved_bookmark", bookMarkDetail.bookmarkName);
                    }
                    const updatedBookmarkList = JSON.parse(JSON.stringify(bookmarkList));
                    updatedBookmarkList[index].usedCount = tagBookmarkData?.data?.usedcount;
                    setBookmarkList(updatedBookmarkList);
                    CustomToast.success(localisedValues["bookmark_saved"]);
                }
            }
        } else if (tagBookmarkError) {
            LogFirebase("Bookmarks", "save_bookmark_error", tagBookmarkError);
        }
    }, [tagBookmarkData, tagBookmarkError]);

    useEffect(() => {
        if (bookmarkWithImage) {
            if (bookmarkWithImage?.success > 0) {
                handleUpdateCount(bookmarkWithImage?.bid, bookmarkWithImage?.usedcount);
            }
            if (bookmarkWithImage.status == UPLOAD_IMAGE_SUCCESS) {
                handleUpdateCount(bookmarkWithImage?.bid, bookmarkWithImage?.usedCount);
            }
        }
    }, [bookmarkWithImage]);

    const handleUpdateCount = (bid, bCount) => {
        const index = bookmarkList?.findIndex((element) => element?.bookmarkId == bid);
        if (index >= 0) {
            const updatedBookmarkList = JSON.parse(JSON.stringify(bookmarkList));
            updatedBookmarkList[index].usedCount = parseInt(bCount);
            setBookmarkList(updatedBookmarkList);
            CustomToast.success(localisedValues["bookmark_saved_with_image"]);
        }
    };

    /* Handle bookmarkList in reducer to handle the count in bookmark button */
    useEffect(() => {
        const bookmarkListCopy = JSON.parse(JSON.stringify(bookmarkList));
        dispatch({ event: BOOKMARK_EVENTS.BOOKMARK_LIST, type: "", payload: bookmarkListCopy });
    }, [bookmarkList]);

    const getBookmarkHandler = () => {
        dispatch(getBookmark());
    };

    const bookmarkTextChangeHandler = (text) => {
        setBookmarkText(text);
    };

    const clearBookmarkText = () => {
        setBookmarkText("");
    };

    const addBookmarkHandler = () => {
        const payload = {
            bookmarkName: bookmarkText,
        };
        dispatch(addBookmark(payload));
    };

    /* Handle to show bookmark list HTML */
    const bookmarkListHTML = useMemo(() => {
        const filteredBookmarks = bookmarkList.filter((bookmark) => bookmark.bookmarkName.toLowerCase().includes(bookmarkText.toLowerCase()));
        console.log("filteredBookmarks", filteredBookmarks);
        return filteredBookmarks?.map((bookmark) => (
            <div className='bookmark-box' key={bookmark?.bookmarkId}>
                <button className='btn bookmark-list-btn' onClick={() => tagBookmarkHandler(bookmark)}>
                    <div className='bookmark-name'>{bookmark?.bookmarkName}</div>
                    {bookmark?.usedCount > 0 && <div className='bookmark-used-count'>{bookmark?.usedCount}</div>}
                </button>
            </div>
        ));
    }, [bookmarkList, bookmarkText, screenSharingUsers, videoStreamingUsers, allowTakePhoto]);

    const tagBookmarkHandler = useCallback(
        (bookmark) => {
            const isvideoenabled = !isEmpty(screenSharingUsers) || !isEmpty(videoStreamingUsers);

            const payload = {
                Bid: bookmark?.bookmarkId,
                CallTime: totalTimeInSeconds(callDuration),
                SessionId: GLOBAL_MEETING_DATA.sessionId,
                MemberId: CALL_DATA.memberId,
                RoomSid: CALL_DATA.roomSID,
            };
            if (PublicVariables.featureFlag.BOOKMARKSAVEIMAGE === ENABLED && allowTakePhoto && isvideoenabled) {
                takePhotoService.takePhoto(bookmark);
            } else {
                dispatch(tagBookmark(payload));
            }
        },
        [allowTakePhoto, screenSharingUsers, videoStreamingUsers]
    );

    const bookMarkHTMLORNewBookmarkHTML = () => {
        if (bookmarkListHTML?.length > 0) {
            return <div className='bookmark-list'>{bookmarkListHTML}</div>;
        } else {
            return (
                <div className='no-sidebar-data'>
                    <div className='no-sidebar-data-in'>
                        <div className='no-sidebar-data-box text-center'>
                            <div className='no-data-icon'>
                                <span className='for-icon icon-no-bookmark'></span>
                            </div>
                            <div className='no-data-text'>
                                <p>{localisedValues["press_enter_or_click_on_create_new_bookmark"]}</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    };

    const searchOrCloseIconHTML = () => {
        if (bookmarkText.length > 0) {
            return (
                <button className='btn clear-search-btn' onClick={() => clearBookmarkText()}>
                    <span className='for-icon icon-close-light'></span>
                </button>
            );
        } else {
            return <span className='frm-search-icon icon-search'></span>;
        }
    };

    return (
        <div className={clsx("sidebar left-sidebar", isBookmarkOpen ? "active" : "")} id='bookmark-sidebar' data-testid='bookmarkSidebar'>
            <div className='close-in-mobile' onClick={() => dispatch(setIsBookmarkOpen(false))} onKeyDown={() => {}}></div>
            <div className='sidebar-in'>
                <div className='sidebar-head'>
                    <div className='sidebar-head-title'>
                        <div className='title' data-testid='bookmarksTitle'>
                            {localisedValues["bookmarks_title"]}
                        </div>
                    </div>
                    <div className='sidebar-head-right'>
                        <div className='sidebar-close'>
                            <button className='btn close-sidebar-button' onClick={() => dispatch(setIsBookmarkOpen(!isBookmarkOpen))}>
                                <span className='for-icon icon-close-light'></span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className='sidebar-body padding-0'>
                    <div className='bookmark'>
                        {!getBookmarkError && !getBookmarkLoading && (
                            <div className='bookmark-head'>
                                <div className='search-box swme-form-group bookmark-search swme-tabbar-form-group'>
                                    <div className='swme-form-group-in'>
                                        {searchOrCloseIconHTML()}
                                        <input
                                            type='text'
                                            autoComplete='off'
                                            className='swme-form-control form-control-sm'
                                            data-testid='bookmarkSearchInpuut'
                                            placeholder={localisedValues["search_or_add_new_bookmark"]}
                                            value={bookmarkText}
                                            maxLength='50'
                                            onChange={(e) => bookmarkTextChangeHandler(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault(); // Prevent default behavior of form submission
                                                    addBookmarkHandler();
                                                }
                                            }}
                                            style={{ fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                                        />
                                        <span className='for-bg'></span>
                                        <span className='for-border'></span>
                                    </div>
                                    {bookmarkListHTML?.length === 0 && bookmarkText?.length > 0 && (
                                        <div
                                            className='create-bookmark-label'
                                            role='button'
                                            tabIndex='0'
                                            onClick={() => addBookmarkHandler()}
                                            onKeyDown={() => {}}
                                        >
                                            <div className='create-bookmark-label-in'>
                                                <div className='for-bookmark-text'>
                                                    {localisedValues["create_new_bookmark"]}: {bookmarkText}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className='bookmark-body custom-scroll'>
                            {!getBookmarkError && !getBookmarkLoading ? (
                                bookMarkHTMLORNewBookmarkHTML()
                            ) : (
                                <div className='no-sidebar-data' data-testid='noSidebarData'>
                                    <div className='no-sidebar-data-in'>
                                        <div className='no-sidebar-data-box text-center'>
                                            <div className='no-data-text'>
                                                <p>
                                                    {localisedValues["tap_retry_to_load"]} {localisedValues["bookmarks_title"]}
                                                </p>
                                            </div>
                                            <div className='button-inline d-flex justify-content-center'>
                                                <div className='button'>
                                                    <button className='btn btn-outline btn-tabbar-outline' onClick={() => getBookmarkHandler()}>
                                                        <span className='btn-text'>{localisedValues["retry"]}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookmarkDrawer;
